import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit as limitQuery,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";

import { Collections } from "./constants";

/**
 * @typedef { import('./db-models').NoSQLTeamEvent } NoSQLTeamEvent
 */

/**
 * @param {import('firebase/firestore').Firestore} db The firestore database
 * @returns {object} The team events collection access functions
 */
export default function eventsCollectionFns(db) {
  /**
   * Create a new team event in the team's events subcollection
   * @param {string} teamId The team id
   * @param {object} eventData The event data
   * @param {string} eventData.type Event type
   * @param {string} eventData.userId User who triggered event
   * @param {string} [eventData.goalId] Goal ID if applicable
   * @param {string} eventData.weekOf Week identifier (yyyy-MM-dd)
   * @param {object} [eventData.metadata] Additional event metadata
   * @returns {Promise<NoSQLTeamEvent>} The created event
   */
  async function createTeamEvent(teamId, eventData) {
    try {
      const eventsRef = collection(db, Collections.TEAMS, teamId, "events");
      const docRef = await addDoc(eventsRef, {
        ...eventData,
        createdAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        ...eventData,
        createdAt: new Date(),
      };
    } catch (error) {
      console.error("Failed to create team event:", error);
      throw error;
    }
  }

  /**
   * List team events for a specific week
   * @param {string} teamId The team id
   * @param {string} weekOf Week identifier (yyyy-MM-dd)
   * @param {object} [options] Query options
   * @param {number} [options.limit] Max number of events to return (0 = all)
   * @returns {Promise<NoSQLTeamEvent[]>} Array of events
   */
  async function listTeamEvents(teamId, weekOf, options = {}) {
    try {
      const eventsRef = collection(db, Collections.TEAMS, teamId, "events");
      let q = query(
        eventsRef,
        where("weekOf", "==", weekOf),
        orderBy("createdAt", "desc"),
      );

      if (options.limit && options.limit > 0) {
        q = query(q, limitQuery(options.limit));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Failed to list team events:", error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for team events
   * @param {string} teamId The team id
   * @param {string} weekOf Week identifier (yyyy-MM-dd)
   * @param {function(NoSQLTeamEvent[]): void} callback Function to call with updated events
   * @param {function(Error): void} [onError] Optional error callback
   * @returns {function(): void} Unsubscribe function
   */
  function subscribeToTeamEvents(teamId, weekOf, callback, onError) {
    try {
      const eventsRef = collection(db, Collections.TEAMS, teamId, "events");
      const q = query(
        eventsRef,
        where("weekOf", "==", weekOf),
        orderBy("createdAt", "desc"),
      );

      return onSnapshot(
        q,
        (snapshot) => {
          const events = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          callback(events);
        },
        (error) => {
          console.error("Error in team events subscription:", error);
          if (onError) {
            onError(error);
          }
        },
      );
    } catch (error) {
      console.error("Failed to subscribe to team events:", error);
      throw error;
    }
  }

  return {
    createTeamEvent,
    listTeamEvents,
    subscribeToTeamEvents,
  };
}
